import { getAssetsByType } from './assetsService';
import { getUserProfile } from './userService';

const TRANSLATION_ASSET_TYPE = 10;

/**
 * Converte código de idioma para formato do CMS (pt-BR, en-US)
 */
const normalizeLanguage = (lang) => {
  const langMap = {
    'pt': 'pt-BR',
    'en': 'en-US',
    'es': 'es-ES',
    'pt-BR': 'pt-BR',
    'en-US': 'en-US',
    'es-ES': 'es-ES'
  };
  return langMap[lang] || lang;
};

/**
 * Processa os assets do CMS para obter as traduções
 * O campo 'url' do asset contém o JSON de traduções
 */
const processTranslationAsset = async (assets, lang) => {
  try {
    // Busca o asset correspondente ao idioma
    const normalizedLang = normalizeLanguage(lang);
    const asset = assets.find(a => a.language === normalizedLang || a.language === lang);
    
    if (asset && asset.url) {
      const response = await fetch(asset.url);
      const translations = await response.json();
      return translations;
    }
    
    return null;
  } catch (error) {
    console.error(`Error processing translation asset for ${lang}:`, error);
    return null;
  }
};

/**
 * Processa os assets do CMS para obter a configuração de idiomas disponíveis
 */
const processLanguagesConfig = async (assets) => {
  try {
    // Extrai idiomas únicos dos assets
    const languages = [...new Set(assets.map(a => a.language))];
    
    // Identifica o idioma padrão (primeiro pt-BR ou primeiro disponível)
    const defaultLang = languages.find(lang => lang === 'pt-BR' || lang === 'pt') 
                      || languages[0] 
                      || 'pt-BR';
    
    // Normaliza códigos de idioma
    const normalizedLanguages = languages.map(lang => {
      const code = lang.split('-')[0].toLowerCase();
      const name = lang === 'pt-BR' ? 'Português' : 
                   lang === 'en-US' ? 'English' : 
                   lang === 'es-ES' ? 'Español' :
                   lang;
      return { code, name };
    });
    
    return {
      defaultLanguage: defaultLang.split('-')[0].toLowerCase(),
      languages: normalizedLanguages,
    };
  } catch (error) {
    console.error('Error processing languages config:', error);
    return {
      defaultLanguage: 'pt',
      languages: [{ code: 'pt', name: 'Português' }, { code: 'en', name: 'English' }],
    };
  }
};

export const i18nService = {
  async fetchTranslations(clientId, lang, profile = getUserProfile()) {
    try {
      const normalizedLang = normalizeLanguage(lang);
      
      // Busca assets do CMS usando o novo serviço unificado
      const assets = await getAssetsByType(TRANSLATION_ASSET_TYPE, {
        language: normalizedLang,
        profile,
        requireAuth: false, // Translations podem ser públicas
      });

      // Processa o asset para obter as traduções
      const translations = await processTranslationAsset(assets, lang);
      
      return translations;
    } catch (error) {
      console.error(`Error fetching translations for ${lang}:`, error);
      return null;
    }
  },

  async fetchAvailableLanguages(clientId, profile = getUserProfile()) {
    try {
      // Busca todos os assets de tradução (sem filtro de idioma específico)
      // Nota: Pode ser necessário ajustar a API para buscar todos os idiomas
      const assets = await getAssetsByType(TRANSLATION_ASSET_TYPE, {
        language: 'pt-BR', // Busca inicial, mas processa todos os assets retornados
        profile,
        requireAuth: false,
      });

      // Processa os assets para obter a configuração de idiomas
      const languagesConfig = await processLanguagesConfig(assets);
      
      return languagesConfig;
    } catch (error) {
      console.error('Error fetching available languages:', error);
      return {
        defaultLanguage: 'pt',
        languages: [{ code: 'pt', name: 'Português' }, { code: 'en', name: 'English' }],
      };
    }
  }
}; 
